<template>
  <div class="main">
    <div v-if="!initializing" class="container-card">
      <h3 class="text-center text-primary bold">
        {{ $t('botones.modificarReserva') }}
      </h3>
      <div class="py-4">
        <CardInfoExperiencia />
        <CardInfoReserva @limpiar-codigo-descuento="limpiarCodigoDescuentoHandler" />
      </div>

      <CardDatosFiscales
        v-if="shouldShowDatosFiscales"
        ref="datosFiscalesRef"
        @datos-fiscales-selected="handleDatosFiscalesSelected" />

      <CardMetodoPago
        v-if="!isInscripcion"
        :pasarelas-pago="grupo.viaje.pasarelasPago"
        :initial-pasarela-pago="grupo.reservas[0].pasarelaPago"
        :importe-pagar="totalPendiente.value"
        class="mt-2 mb-3"
        @metodo-pago-seleccionado="handleMetodoPagoSeleccionado" />

      <div v-if="!isInscripcion" class="row">
        <div class="col-12 col-md-6 pb-3">
          <template v-if="totalPendiente.value > 0">
            <ButtonCodigoDescuento
              :viaje-id="String(grupo.viaje.id)"
              :reservas-ids="grupo.reservas?.map(reserva => reserva.id)"
              @descuento-aplicado="codigoDescuentoHandler" />
          </template>
        </div>
        <div class="col-12 col-md-6 text-end pb-3">
          <div class="outer-card">
            <p class="p text-primary m-0 p-0 bold importe">
              {{ $t('subtitulo.importePendiente') }}
            </p>
            <div class="inner-card">
              <div class="text-secondary h6 py-2 px-3 price">
                {{ formatPrice(totalPendiente) }} {{ agenciaStore.getCurrency }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end mt-2">
        <MyGocuButton
          v-if="isInscripcion"
          primary
          :label="$t('botones.modificar').toLocaleUpperCase()"
          @click="modificarSinPagar" />
        <MyGocuButton
          v-else
          primary
          :label="$t('botones.modificarPagar').toLocaleUpperCase()"
          @click="registraPagoModificar" />
      </div>
    </div>

    <div v-if="infoPagoRedsys">
      <form name="redsysPagoForm" :action="infoPagoRedsys.url" method="POST">
        <input type="hidden" name="Ds_SignatureVersion" :value="infoPagoRedsys.signatureVersion" />
        <input type="hidden" name="Ds_MerchantParameters" :value="infoPagoRedsys.params" />
        <input type="hidden" name="Ds_Signature" :value="infoPagoRedsys.signature" />
      </form>
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, nextTick, onBeforeMount } from 'vue';
  import ReservasApiService from '@/services/api/ReservasApiService';
  import PasarelaPagoApiService from '@/services/api/PasarelaPagoApiService';
  import { useRoute, useRouter } from 'vue-router';
  import ButtonCodigoDescuento from '@/modules/viajesGrupos/components/ButtonCodigoDescuento.vue';
  import { controlErrorRealizarPago } from '@/services/utils/ErrorControl';
  import CardInfoExperiencia from '@/components/card/Reserva/CardInfoExperiencia.vue';
  import CardInfoReserva from '@/components/card/Reserva/CardInfoReserva.vue';
  import CardDatosFiscales from '@/modules/viajesGrupos/components/CardDatosFiscales.vue';
  import { informacionHabitacion } from '@/store_pinia/informacionHabitacion';
  import { UsuarioProximasReservasDetalle } from '@/router/RouterNames';
  import { ESTADOS_INVITACION_GRUPO, TIPOS_PAGO } from '@/constants/viajeConstants';
  import { ESTADOS_RESERVA } from '@/constants/reservaConstants';
  import { agenciaStoreModule } from '@/store_pinia/agencia';
  import { toRawDeep } from '@/helpers/reactivityUtils';
  import currency from 'currency.js';
  import { useLoadingStore } from '@/store_pinia/loadingStore';
  import CardMetodoPago from '@/components/card/Reserva/CardMetodoPago.vue';
  import { formatPrice } from '@/helpers/numberUtils';
  import MyGocuButton from '@/components/common/buttons/MyGocuButton.vue';

  const route = useRoute();
  const router = useRouter();
  const loadingStore = useLoadingStore();
  const storeHabitacion = informacionHabitacion();
  const agenciaStore = agenciaStoreModule();
  const grupo = ref();
  const initializing = ref(true);
  const infoPagoRedsys = ref(null);
  const datosFiscalesRef = ref(null);
  const selectedDatosFiscalesId = ref(null);
  const selectedPasarelaPago = ref(null);

  const reservasInDraft = computed(() =>
    storeHabitacion.getResumenReservas.filter(
      reserva => reserva.estado !== ESTADOS_RESERVA.CANCELADA && reserva.withDraft
    )
  );
  const isInscripcion = computed(
    () =>
      grupo.value.viaje.tipoPago === TIPOS_PAGO.SIN_PAGO ||
      grupo.value.viaje.invitacionGrupo?.estado === ESTADOS_INVITACION_GRUPO.ACEPTADA
  );
  const totalPendiente = computed(() => {
    let totalPendienteAux = currency(0);
    storeHabitacion.getResumenReservas.map(({ precioTotal, totalPagado, descuento }) => {
      let valorPendiente = currency(precioTotal).subtract(totalPagado).subtract(descuento);
      totalPendienteAux = totalPendienteAux.add(Math.max(0, valorPendiente.value));
    });
    return totalPendienteAux;
  });

  const shouldShowDatosFiscales = computed(() => grupo.value.viaje.datosFiscalesEnabled && !isInscripcion.value);

  function handleDatosFiscalesSelected(id) {
    selectedDatosFiscalesId.value = id;
  }

  function handleMetodoPagoSeleccionado(pasarelaPago) {
    selectedPasarelaPago.value = pasarelaPago;
  }

  function moveToMisReservas() {
    router.push({
      name: UsuarioProximasReservasDetalle,
      params: { grupoId: grupo.value.id },
    });
  }

  async function registraPagoModificar() {
    try {
      loadingStore.show();

      if (shouldShowDatosFiscales.value) {
        if (selectedDatosFiscalesId.value === null) {
          selectedDatosFiscalesId.value = await datosFiscalesRef.value.submit();
        }
        if (!selectedDatosFiscalesId.value || selectedDatosFiscalesId.value === null) {
          return;
        }
      }

      const reservasId = reservasInDraft.value.map(reserva => reserva.id);
      const datosRedireccionDTO = await PasarelaPagoApiService.solicitaCobroSucesivo(
        reservasId,
        true,
        storeHabitacion.getCodigoDescuento?.codigo,
        TIPOS_PAGO.ALL,
        selectedDatosFiscalesId.value,
        selectedPasarelaPago.value?.id
      );
      if (!datosRedireccionDTO.url) {
        moveToMisReservas();
      } else {
        if (datosRedireccionDTO.redsysInfo) {
          infoPagoRedsys.value = datosRedireccionDTO.redsysInfo;
          infoPagoRedsys.value.url = datosRedireccionDTO.url;
          await nextTick();
          document.redsysPagoForm.submit();
        } else {
          window.location.replace(datosRedireccionDTO.url);
        }
      }
      storeHabitacion.setCodigoDescuento(null);
    } catch (err) {
      console.error(err);
      controlErrorRealizarPago(err);
    } finally {
      loadingStore.hide();
    }
  }

  async function modificarSinPagar() {
    try {
      loadingStore.show();
      let reservasId = reservasInDraft.value.map(reserva => reserva.id);
      await PasarelaPagoApiService.solicitaContinuarSinPagar(reservasId);
      nextTick(() => {
        moveToMisReservas();
      });
    } catch (err) {
      console.error(err);
      controlErrorRealizarPago(err);
    } finally {
      loadingStore.hide();
    }
  }

  function codigoDescuentoHandler(informacionCodigoDescuento) {
    storeHabitacion.setCodigoDescuento(informacionCodigoDescuento);
  }

  function limpiarCodigoDescuentoHandler() {
    storeHabitacion.setCodigoDescuento(null);
  }

  async function init() {
    try {
      loadingStore.show();
      grupo.value = await ReservasApiService.getGrupo(route.params.grupoId, true);

      storeHabitacion.clearReservas();
      const grupoRaw = toRawDeep(grupo.value);
      storeHabitacion.setInformacionAlojamientoFromRGrupoDTO(grupoRaw);
      if (grupoRaw.viaje && grupoRaw.viaje.id) {
        await storeHabitacion.loadInformacionViaje(
          grupoRaw.viaje.id,
          grupoRaw.viaje.codigo ?? null,
          false,
          grupoRaw.viaje?.codigoPrevisualizacion ?? null
        );
      }
      grupoRaw.reservas.forEach(reserva => {
        reserva.usuario = {
          nombre: reserva.nombre,
          apellidos: reserva.apellidos,
          apellido: reserva.apellidos,
          fechaNacimiento: reserva.fechaNacimiento,
        };
        storeHabitacion.addReserva(reserva);
        selectedPasarelaPago.value = grupoRaw.reservas[0].pasarelaPago;
      });
    } catch (error) {
      console.error(error);
    } finally {
      initializing.value = false;
      loadingStore.hide();
    }
  }

  onBeforeMount(async () => await init());
</script>

<style scoped>
  .container-card {
    max-width: 1200px;
    margin: auto;
    padding-top: 14rem;
    padding-bottom: 4rem;
  }

  .main {
    background-size: cover;
    background-repeat: no-repeat;
    margin: 0 35px;
  }

  .row {
    display: flex;
    flex-wrap: wrap;
  }

  .col-md-6 {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  @media (max-width: 1000px) {
    .col-12 {
      width: 100%;
      display: block;
    }

    .text-end {
      text-align: start;
    }

    .container-card {
      padding-top: 6rem;
    }

    .importe,
    .price {
      font-size: 15px !important;
    }
  }

  .outer-card {
    width: 100%;
    background-color: white;
    position: relative;
    border: 1px solid #ccc;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 20px;
    height: 85px;
    border-radius: var(--bs-border-radius-xl);
    box-shadow: 0px 2px 4px 0px #00000080;
  }

  .inner-card {
    width: 30%;
    height: 100%;
    position: absolute;
    right: 0;

    .price {
      background-color: var(--bs-primary);
      border-radius: var(--bs-border-radius-xl);
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      box-shadow: 0px 2px 4px 0px #00000080;
      border-left: 1px solid #ddd;
    }
  }
</style>
